import React from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useSnackbar } from 'notistack';
import TransactionForm from '../TransactionForm';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import { transactions as transactionsApi } from '@afosto/order/api/Transactions';
import translations from './translations';

const CreateTransactionDrawer = ({
  isOpen,
  onClose,
  onCreate,
  amount,
  description: providedDescription,
  client: providedClient,
  entity,
}) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  /**
   * Handle creating a new transaction.
   * @param {string} description
   * @param {number} transactionAmount
   * @param {string} currency
   * @param {string} webhookUrl
   * @param {object} client
   * @returns {Promise}
   */
  const handleCreateTransaction = async ({
    description,
    amount: transactionAmount,
    currency,
    webhookUrl,
    client,
  }) => {
    try {
      const response = await transactionsApi.createTransaction({
        reference: {
          entity_type: entity?.type,
          entity_id: entity?.id,
        },
        description,
        amount: transactionAmount,
        currency,
        webhook_url: webhookUrl,
        client_id: client?.id ?? null,
      });
      enqueueSnackbar(intl.formatMessage(translations.feedback.transactionCreated), {
        variant: 'success',
      });

      onCreate(response);

      return response.data;
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.transactionNotCreated), {
        variant: 'error',
      });
      return Promise.reject(err);
    }
  };

  return (
    <FormDrawer
      title={intl.formatMessage(translations.title)}
      isOpen={isOpen}
      onClose={onClose}
      drawerWidth="400px"
    >
      <TransactionForm
        onSubmit={handleCreateTransaction}
        onCancel={onClose}
        transaction={{ amount, description: providedDescription, client: providedClient }}
      />
    </FormDrawer>
  );
};

CreateTransactionDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onCreate: PropTypes.func,
  amount: PropTypes.number,
  description: PropTypes.string,
  client: PropTypes.object,
  entity: PropTypes.object,
};

CreateTransactionDrawer.defaultProps = {
  isOpen: false,
  onClose: undefined,
  onCreate: undefined,
  amount: null,
  description: null,
  client: undefined,
  entity: undefined,
};

export default CreateTransactionDrawer;
